import { useEffect, useMemo, useRef, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'
import { Check, GripVertical, Pencil, Trash2, X } from 'lucide-react'

import { UnderlineInput } from '@/components/ui/underline-input'
import type { FollowUpCategory } from '@/db/domain/followUps'
import { useDb } from '@/lib/dbContext'

/**
 * Admin curation of the follow-up category list (follow_up_categories).
 * Compact rows: drag the grip to reorder, rename via the pencil, delete via
 * the bin. Staff pick from this list when filing a follow-up; deleting a
 * category leaves existing follow-ups untouched (they read as uncategorised).
 */
export function FollowUpCategoriesSection() {
  const db = useDb()
  const categoriesResult = useLiveQuery((q) =>
    q.from({ categories: db.followUpCategoriesCollection }),
  )

  useEffect(() => {
    void db.followUpCategoriesCollection.preload?.()
  }, [db])

  const categories = useMemo(
    () =>
      [...((categoriesResult.data ?? []) as FollowUpCategory[])].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      ),
    [categoriesResult.data],
  )

  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Drag state: the dragged row id and where it currently hovers. The list
  // renders in the previewed order while dragging, so the drop feels direct.
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const displayed = useMemo(() => {
    if (draggingId === null || dragOverIndex === null) return categories
    const from = categories.findIndex((c) => c.id === draggingId)
    if (from === -1) return categories
    const next = [...categories]
    const [moved] = next.splice(from, 1)
    next.splice(Math.min(dragOverIndex, next.length), 0, moved)
    return next
  }, [categories, draggingId, dragOverIndex])

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not save the change. Please try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  const endDrag = () => {
    setDraggingId(null)
    setDragOverIndex(null)
  }

  const commitDrag = () => {
    const order = displayed.map((c) => c.id)
    const changed = categories.some((c, i) => c.id !== order[i])
    endDrag()
    if (changed) void run(() => db.reorderFollowUpCategories(order))
  }

  const handleAdd: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    if (!newName.trim() || busy) return
    void run(async () => {
      await db.createFollowUpCategory(newName)
      setNewName('')
    })
  }

  return (
    <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
      <div className="max-w-xl space-y-1">
        <h2 className="text-base font-semibold text-slate-900">
          Follow-up categories
        </h2>
        <p className="text-[13px] text-slate-500">
          The list staff pick from when filing a follow-up. Drag to arrange.
          Deleting a category keeps its follow-ups — they just show without a
          category.
        </p>
      </div>

      <ul className="max-w-md overflow-hidden rounded-md border border-slate-200">
        {displayed.map((category, index) => (
          <CategoryRow
            key={category.id}
            category={category}
            isEditing={editingId === category.id}
            isDragging={draggingId === category.id}
            disabled={busy}
            dragDisabled={busy || editingId !== null}
            onDragStart={() => {
              setDraggingId(category.id)
              setDragOverIndex(index)
            }}
            onDragOver={() => {
              if (draggingId !== null && dragOverIndex !== index)
                setDragOverIndex(index)
            }}
            onDrop={commitDrag}
            onDragEnd={endDrag}
            onStartEdit={() => setEditingId(category.id)}
            onCancelEdit={() => setEditingId(null)}
            onRename={(name) => {
              setEditingId(null)
              if (name.trim() && name.trim() !== category.name)
                void run(() => db.renameFollowUpCategory(category.id, name))
            }}
            onDelete={() => {
              const confirmed = window.confirm(
                `Delete the “${category.name}” category? Existing follow-ups keep working and will show without a category.`,
              )
              if (confirmed) void run(() => db.deleteFollowUpCategory(category.id))
            }}
          />
        ))}
        {displayed.length === 0 && categoriesResult.isReady ? (
          <li className="px-3 py-2.5 text-[13px] text-slate-500">
            No categories yet — add the first one below.
          </li>
        ) : null}
      </ul>

      <form onSubmit={handleAdd} className="flex max-w-md items-center gap-2">
        <div className="flex-1">
          <UnderlineInput
            aria-label="New category name"
            placeholder="New category name"
            value={newName}
            disabled={busy}
            onChange={(event) => setNewName(event.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={busy || !newName.trim()}
          className="inline-flex h-8 items-center rounded-md bg-slate-900 px-3 text-[12.5px] font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add
        </button>
      </form>

      {error ? <p className="text-[13px] text-rose-600">{error}</p> : null}
    </section>
  )
}

const iconButtonClass =
  'inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40'

function CategoryRow(props: {
  category: FollowUpCategory
  isEditing: boolean
  isDragging: boolean
  disabled: boolean
  dragDisabled: boolean
  onDragStart: () => void
  onDragOver: () => void
  onDrop: () => void
  onDragEnd: () => void
  onStartEdit: () => void
  onCancelEdit: () => void
  onRename: (name: string) => void
  onDelete: () => void
}) {
  const { category } = props
  const [draft, setDraft] = useState(category.name)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (props.isEditing) {
      setDraft(category.name)
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [props.isEditing, category.name])

  return (
    <li
      draggable={!props.dragDisabled}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move'
        // Some browsers need data set for the drag to start.
        event.dataTransfer.setData('text/plain', category.id)
        props.onDragStart()
      }}
      onDragOver={(event) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        props.onDragOver()
      }}
      onDrop={(event) => {
        event.preventDefault()
        props.onDrop()
      }}
      onDragEnd={props.onDragEnd}
      className={`flex items-center gap-1.5 border-b border-slate-100 px-2 py-1.5 text-[13px] text-slate-800 last:border-b-0 ${
        props.isDragging ? 'bg-slate-50 opacity-60' : 'bg-white'
      }`}
    >
      <span
        aria-hidden
        className={`text-slate-300 ${props.dragDisabled ? '' : 'cursor-grab active:cursor-grabbing'}`}
      >
        <GripVertical className="h-4 w-4" />
      </span>

      {props.isEditing ? (
        <>
          <input
            ref={inputRef}
            aria-label={`Rename ${category.name}`}
            value={draft}
            disabled={props.disabled}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                props.onRename(draft)
              }
              if (event.key === 'Escape') props.onCancelEdit()
            }}
            className="h-7 flex-1 rounded border border-slate-300 bg-white px-2 text-[13px] outline-none focus:border-slate-500"
          />
          <button
            type="button"
            aria-label={`Save name for ${category.name}`}
            disabled={props.disabled || !draft.trim()}
            onClick={() => props.onRename(draft)}
            className={iconButtonClass}
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Cancel rename"
            onClick={props.onCancelEdit}
            className={iconButtonClass}
          >
            <X className="h-4 w-4" />
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 truncate">{category.name}</span>
          <button
            type="button"
            aria-label={`Rename ${category.name}`}
            title="Rename"
            disabled={props.disabled}
            onClick={props.onStartEdit}
            className={iconButtonClass}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={`Delete ${category.name}`}
            title="Delete"
            disabled={props.disabled}
            onClick={props.onDelete}
            className={`${iconButtonClass} hover:text-rose-600`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </li>
  )
}
